import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRightLeft, Eye, FileSearch, History, Layers3 } from 'lucide-react';

const toneByDelta = (value) => {
  if (!value) return 'outline';
  return value > 0 ? 'default' : 'secondary';
};

export const TemplateStoredEffectiveComparator = ({
  selectedTemplate,
  compareData,
  loading,
  storedPreviewUrl,
  effectivePreviewUrl,
  previewLoading,
  previewMode,
  onPreviewModeChange,
  auditLogs,
  auditLoading,
  onFocusEffectiveTemplate,
}) => {
  if (!selectedTemplate) return null;

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm" data-testid="template-stored-effective-loading">
        <CardContent className="px-4 py-6 text-sm text-slate-500">A carregar comparador stored vs effective do template...</CardContent>
      </Card>
    );
  }

  if (!compareData?.stored || !compareData?.effective) {
    return (
      <Card className="border-slate-200 shadow-sm" data-testid="template-stored-effective-empty">
        <CardContent className="px-4 py-6 text-sm text-slate-500">Sem dados de comparação para o template seleccionado.</CardContent>
      </Card>
    );
  }

  const stored = compareData.stored;
  const effective = compareData.effective;
  const runtime = compareData.runtime || {};
  const diffSummary = compareData.diff_summary || {};
  const previewRisks = compareData.preview_risks || {};
  const sameTemplate = stored.id === effective.id;

  const summaryCards = [
    { label: 'Δ Elementos', value: diffSummary.element_delta || 0 },
    { label: 'Δ Bindings', value: diffSummary.binding_delta || 0 },
    { label: 'Δ Datasources', value: diffSummary.datasource_delta || 0 },
    { label: 'Ajustes runtime', value: (runtime.adjustments || []).length },
  ];

  const latestAudit = auditLogs?.[0] || null;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="template-stored-effective-comparator">
      <CardHeader className="px-4 py-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ArrowRightLeft className="h-4 w-4 text-slate-700" />
              Comparador visual `stored vs effective`
            </CardTitle>
            <CardDescription>
              Template em foco: <strong>{selectedTemplate.name}</strong>. Mostra o guardado, o template efectivamente resolvido, os ajustes runtime e previews lado a lado.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={sameTemplate ? 'secondary' : 'default'} data-testid="template-compare-resolution-badge">
              {sameTemplate ? 'stored = effective' : 'stored ≠ effective'}
            </Badge>
            {!sameTemplate && (
              <Button type="button" variant="outline" size="sm" className="h-8 px-3 text-[11px]" onClick={onFocusEffectiveTemplate} data-testid="template-compare-focus-effective-button">
                Abrir template efectivo
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-4 pb-4 pt-0">
        <div className="grid gap-3 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2" data-testid="template-compare-summary-grid">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Stored</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{stored.name}</p>
                <p className="mt-1 text-xs text-slate-600">{stored.module_group} · {stored.location_name || 'Global'}</p>
                <div className="mt-3 grid gap-1 text-xs text-slate-600">
                  <p>Elementos: <strong>{stored.layout?.element_count || 0}</strong></p>
                  <p>Bindings: <strong>{stored.layout?.variable_binding_count || 0}</strong></p>
                  <p>Unresolved: <strong>{stored.layout?.unresolved_variable_count || 0}</strong></p>
                  <p>Overlaps guardados: <strong>{(stored.layout?.overlap_candidates || []).length}</strong></p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Effective</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{effective.name}</p>
                <p className="mt-1 text-xs text-slate-600">{effective.module_group} · regra {effective.resolved_rule || 'n/d'}</p>
                <div className="mt-3 grid gap-1 text-xs text-slate-600">
                  <p>Elementos: <strong>{effective.layout?.element_count || 0}</strong></p>
                  <p>Bindings: <strong>{effective.layout?.variable_binding_count || 0}</strong></p>
                  <p>Unresolved: <strong>{effective.layout?.unresolved_variable_count || 0}</strong></p>
                  <p>Overlaps efectivos: <strong>{(effective.layout?.overlap_candidates || []).length}</strong></p>
                </div>
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="template-compare-deltas-grid">
              {summaryCards.map((item) => (
                <div key={item.label} className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{item.label}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-lg font-semibold text-slate-900">{item.value}</p>
                    <Badge variant={toneByDelta(item.value)}>{item.value ? 'atenção' : 'estável'}</Badge>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="template-compare-actionable-diff">
              <div className="flex items-center gap-2">
                <FileSearch className="h-4 w-4 text-slate-600" />
                <p className="text-sm font-semibold text-slate-900">Diff accionável</p>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-white bg-white p-3">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Riscos detectados</p>
                  <ul className="mt-2 space-y-1 text-xs text-slate-700">
                    {(previewRisks.root_causes || []).length ? previewRisks.root_causes.map((item) => <li key={item}>• {item}</li>) : <li>• Nenhum risco crítico listado</li>}
                  </ul>
                </div>
                <div className="rounded-xl border border-white bg-white p-3">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Ajustes runtime activos</p>
                  <ul className="mt-2 space-y-1 text-xs text-slate-700">
                    {(runtime.adjustments || []).length ? runtime.adjustments.slice(0, 6).map((item) => <li key={`${item.kind}-${item.message}`}>• {item.message || item.kind}</li>) : <li>• Sem ajustes runtime neste template</li>}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="template-compare-audit-panel">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-slate-600" />
              <p className="text-sm font-semibold text-slate-900">Auditoria rápida por template</p>
            </div>
            <p className="mt-1 text-xs text-slate-500">Últimas resoluções reais guardadas no runtime de impressão.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant={previewMode === 'real' ? 'default' : 'outline'} className="h-8 px-3 text-[11px]" onClick={() => onPreviewModeChange('real')} disabled={!latestAudit} data-testid="template-compare-mode-real-button">
                Validar com último real
              </Button>
              <Button type="button" size="sm" variant={previewMode === 'sample' ? 'default' : 'outline'} className="h-8 px-3 text-[11px]" onClick={() => onPreviewModeChange('sample')} data-testid="template-compare-mode-sample-button">
                Validar com amostra
              </Button>
            </div>

            <div className="mt-3 space-y-2">
              {auditLoading ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-4 text-xs text-slate-500">A carregar auditoria por template...</div>
              ) : auditLogs?.length ? auditLogs.slice(0, 5).map((log) => (
                <div key={log.id} className="rounded-xl border border-white bg-white px-3 py-2 text-xs text-slate-700" data-testid={`template-compare-audit-log-${log.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-slate-900">{log.doc_type} · {log.resolved_template_name}</p>
                    <Badge variant="outline">{log.module_group || 'módulo n/d'}</Badge>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">{log.timestamp}</p>
                  <p className="mt-1 text-[11px]">Local: {log.location_id || 'global'} · regra: {log.resolved_template_rule || 'n/d'}</p>
                </div>
              )) : (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-4 text-xs text-slate-500">Sem auditoria recente para este template.</div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="template-compare-previews-panel">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-slate-600" />
            <p className="text-sm font-semibold text-slate-900">Validação visual por template</p>
          </div>
          <p className="mt-1 text-xs text-slate-500">Modo actual: {previewMode === 'real' && latestAudit ? 'último documento real' : 'amostra controlada'}.</p>
          <div className="mt-3 grid gap-3 xl:grid-cols-2">
            {[
              { key: 'stored', label: 'Stored guardado', url: storedPreviewUrl },
              { key: 'effective', label: 'Effective resolvido', url: effectivePreviewUrl },
            ].map((item) => (
              <div key={item.key} className="rounded-2xl border border-slate-200 bg-slate-50 p-2.5" data-testid={`template-compare-preview-${item.key}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-slate-900">{item.label}</p>
                  <Badge variant="outline">PDF</Badge>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  {previewLoading ? (
                    <div className="flex h-[420px] items-center justify-center text-xs text-slate-500">A gerar preview...</div>
                  ) : item.url ? (
                    <iframe src={item.url} title={item.label} className="h-[420px] w-full" data-testid={`template-compare-preview-frame-${item.key}`} />
                  ) : (
                    <div className="flex h-[420px] items-center justify-center text-xs text-slate-500">Sem preview disponível neste modo.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="template-compare-footer-notes">
          <div className="flex items-start gap-2">
            <Layers3 className="mt-0.5 h-4 w-4 text-slate-500" />
            <div>
              <p><strong>Leitura rápida:</strong> stored mostra o template editado; effective mostra o template realmente resolvido pelo runtime; auditoria mostra o último uso real; previews permitem validação visual antes de mexer em qualquer template produtivo.</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TemplateStoredEffectiveComparator;